import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Compass, Lightbulb, ArrowRight, CheckCircle2 } from 'lucide-react';
type TestType = 'riasec' | 'learning-style' | 'aptitude' | null;
export function Tests() {
  const [activeTest, setActiveTest] = useState<TestType>(null);
  const [testStep, setTestStep] = useState<'intro' | 'questions' | 'result'>('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  // Mock questions for RIASEC
  const riasecQuestions = ['Saya suka merakit atau memperbaiki barang.', 'Saya suka menganalisis masalah dan mencari solusinya.', 'Saya suka mengekspresikan diri melalui seni, musik, atau tulisan.', 'Saya suka membantu orang lain menyelesaikan masalah mereka.', 'Saya suka memimpin kelompok dan meyakinkan orang lain.', 'Saya suka mengatur data, dokumen, atau jadwal dengan rapi.'];
  const handleStartTest = (type: TestType) => {
    setActiveTest(type);
    setTestStep('intro');
    setCurrentQuestion(0);
  };
  const handleAnswer = () => {
    if (currentQuestion < riasecQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setTestStep('result');
    }
  };
  const renderTestSelection = () => <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-522">
      <motion.div whileHover={{
      y: -5
    }} className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 text-center" data-id="element-523">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-524">
          <Compass className="text-blue-600" size={40} data-id="element-525" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-526">Tes RIASEC</h3>
        <p className="text-gray-600 mb-6 text-sm" data-id="element-527">
          Temukan tipe kepribadian kariermu (Realistic, Investigative, Artistic,
          Social, Enterprising, Conventional).
        </p>
        <button onClick={() => handleStartTest('riasec')} className="w-full bg-blue-50 text-blue-600 font-semibold py-3 rounded-xl hover:bg-blue-600 hover:text-white transition-colors" data-id="element-528">
          Mulai Tes
        </button>
      </motion.div>

      <motion.div whileHover={{
      y: -5
    }} className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 text-center" data-id="element-529">
        <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-530">
          <Brain className="text-pink-600" size={40} data-id="element-531" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-532">
          Tes Gaya Belajar
        </h3>
        <p className="text-gray-600 mb-6 text-sm" data-id="element-533">
          Ketahui apakah kamu tipe pembelajar Visual, Auditori, atau Kinestetik
          untuk strategi belajar yang lebih efektif.
        </p>
        <button onClick={() => handleStartTest('learning-style')} className="w-full bg-pink-50 text-pink-600 font-semibold py-3 rounded-xl hover:bg-pink-600 hover:text-white transition-colors" data-id="element-534">
          Mulai Tes
        </button>
      </motion.div>

      <motion.div whileHover={{
      y: -5
    }} className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 text-center" data-id="element-535">
        <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-536">
          <Lightbulb className="text-purple-600" size={40} data-id="element-537" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-538">
          Tes Minat Bakat
        </h3>
        <p className="text-gray-600 mb-6 text-sm" data-id="element-539">
          Eksplorasi potensi terpendammu untuk membantu menentukan jurusan
          kuliah atau jalur karier yang tepat.
        </p>
        <button onClick={() => handleStartTest('aptitude')} className="w-full bg-purple-50 text-purple-600 font-semibold py-3 rounded-xl hover:bg-purple-600 hover:text-white transition-colors" data-id="element-540">
          Mulai Tes
        </button>
      </motion.div>
    </div>;
  const renderActiveTest = () => {
    if (testStep === 'intro') {
      return <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto text-center" data-id="element-541">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-542">
            {activeTest === 'riasec' ? 'Tes RIASEC (Holland Code)' : activeTest === 'learning-style' ? 'Tes Gaya Belajar' : 'Tes Minat Bakat'}
          </h2>
          <p className="text-gray-600 mb-8 text-lg" data-id="element-543">
            Tes ini berisi beberapa pernyataan. Pilih seberapa setuju kamu
            dengan pernyataan tersebut. Jawablah dengan jujur sesuai dengan apa
            yang kamu rasakan, bukan apa yang kamu anggap baik.
          </p>
          <div className="bg-blue-50 p-6 rounded-2xl mb-8 text-left" data-id="element-544">
            <h4 className="font-bold text-blue-800 mb-2" data-id="element-545">Instruksi:</h4>
            <ul className="list-disc list-inside text-blue-700 space-y-2" data-id="element-546">
              <li data-id="element-547">Waktu pengerjaan sekitar 5-10 menit.</li>
              <li data-id="element-548">Tidak ada jawaban benar atau salah.</li>
              <li data-id="element-549">
                Hasil tes akan langsung keluar setelah kamu menyelesaikan semua
                pertanyaan.
              </li>
            </ul>
          </div>
          <div className="flex justify-center gap-4" data-id="element-550">
            <button onClick={() => setActiveTest(null)} className="px-6 py-3 rounded-xl font-semibold text-gray-500 hover:bg-gray-100 transition-colors" data-id="element-551">
              Kembali
            </button>
            <button onClick={() => setTestStep('questions')} className="bg-primary text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center gap-2" data-id="element-552">
              Mulai Sekarang <ArrowRight size={20} data-id="element-553" />
            </button>
          </div>
        </div>;
    }
    if (testStep === 'questions') {
      const progress = (currentQuestion + 1) / riasecQuestions.length * 100;
      return <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto" data-id="element-554">
          <div className="mb-8" data-id="element-555">
            <div className="flex justify-between text-sm text-gray-500 mb-2" data-id="element-556">
              <span data-id="element-557">
                Pertanyaan {currentQuestion + 1} dari {riasecQuestions.length}
              </span>
              <span data-id="element-558">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5" data-id="element-559">
              <div className="bg-primary h-2.5 rounded-full transition-all duration-500" style={{
              width: `${progress}%`
            }} data-id="element-560"></div>
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-561">
            <motion.div key={currentQuestion} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="text-center py-10" data-id="element-562">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-12" data-id="element-563">
                "{riasecQuestions[currentQuestion]}"
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-564">
                <button onClick={handleAnswer} className="py-4 px-6 rounded-xl border-2 border-gray-200 hover:border-primary hover:bg-primary-light/10 font-medium text-gray-700 transition-all" data-id="element-565">
                  Sangat Tidak Setuju
                </button>
                <button onClick={handleAnswer} className="py-4 px-6 rounded-xl border-2 border-gray-200 hover:border-primary hover:bg-primary-light/10 font-medium text-gray-700 transition-all" data-id="element-566">
                  Tidak Setuju
                </button>
                <button onClick={handleAnswer} className="py-4 px-6 rounded-xl border-2 border-gray-200 hover:border-primary hover:bg-primary-light/10 font-medium text-gray-700 transition-all" data-id="element-567">
                  Setuju
                </button>
                <button onClick={handleAnswer} className="py-4 px-6 rounded-xl border-2 border-gray-200 hover:border-primary hover:bg-primary-light/10 font-medium text-gray-700 transition-all" data-id="element-568">
                  Sangat Setuju
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>;
    }
    if (testStep === 'result') {
      return <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 max-w-4xl mx-auto" data-id="element-569">
          <div className="text-center mb-10" data-id="element-570">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-571">
              <CheckCircle2 className="text-green-500" size={40} data-id="element-572" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-573">
              Hasil Tes Kamu
            </h2>
            <p className="text-gray-600" data-id="element-574">
              Berdasarkan jawabanmu, berikut adalah profil dominanmu.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-10" data-id="element-575">
            <div data-id="element-576">
              <h3 className="text-2xl font-bold text-primary-dark mb-4" data-id="element-577">
                Tipe: SOCIAL (S)
              </h3>
              <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-578">
                Kamu memiliki ketertarikan tinggi untuk bekerja dengan orang
                lain. Kamu suka membantu, mendidik, menyembuhkan, atau melayani
                orang lain. Kamu memiliki kemampuan komunikasi yang baik dan
                empati yang tinggi.
              </p>
              <div className="bg-gray-50 p-5 rounded-2xl" data-id="element-579">
                <h4 className="font-bold text-gray-800 mb-2" data-id="element-580">
                  Rekomendasi Jurusan/Karier:
                </h4>
                <ul className="list-disc list-inside text-gray-600 space-y-1" data-id="element-581">
                  <li data-id="element-582">Psikologi / Bimbingan Konseling</li>
                  <li data-id="element-583">Pendidikan / Keguruan</li>
                  <li data-id="element-584">Keperawatan / Kedokteran</li>
                  <li data-id="element-585">Ilmu Komunikasi</li>
                  <li data-id="element-586">Pekerja Sosial</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4" data-id="element-587">
              <div data-id="element-588">
                <div className="flex justify-between text-sm font-bold mb-1" data-id="element-589">
                  <span className="text-blue-600" data-id="element-590">Social (S)</span>
                  <span data-id="element-591">85%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3" data-id="element-592">
                  <div className="bg-blue-500 h-3 rounded-full" style={{
                  width: '85%'
                }} data-id="element-593"></div>
                </div>
              </div>
              <div data-id="element-594">
                <div className="flex justify-between text-sm font-bold mb-1" data-id="element-595">
                  <span className="text-pink-600" data-id="element-596">Artistic (A)</span>
                  <span data-id="element-597">70%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3" data-id="element-598">
                  <div className="bg-pink-500 h-3 rounded-full" style={{
                  width: '70%'
                }} data-id="element-599"></div>
                </div>
              </div>
              <div data-id="element-600">
                <div className="flex justify-between text-sm font-bold mb-1" data-id="element-601">
                  <span className="text-yellow-600" data-id="element-602">Enterprising (E)</span>
                  <span data-id="element-603">60%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3" data-id="element-604">
                  <div className="bg-yellow-500 h-3 rounded-full" style={{
                  width: '60%'
                }} data-id="element-605"></div>
                </div>
              </div>
              <div data-id="element-606">
                <div className="flex justify-between text-sm font-bold mb-1" data-id="element-607">
                  <span className="text-green-600" data-id="element-608">Investigative (I)</span>
                  <span data-id="element-609">45%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3" data-id="element-610">
                  <div className="bg-green-500 h-3 rounded-full" style={{
                  width: '45%'
                }} data-id="element-611"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center" data-id="element-612">
            <button onClick={() => setActiveTest(null)} className="bg-primary text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors" data-id="element-613">
              Selesai & Kembali
            </button>
          </div>
        </div>;
    }
  };
  return <div className="bg-gray-50 min-h-screen py-12" data-id="element-614">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-615">
        {!activeTest && <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-616">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-617">
              Tes Minat & Bakat
            </h1>
            <p className="text-gray-600 text-lg" data-id="element-618">
              Kenali dirimu lebih dalam. Hasil tes ini dapat membantumu
              merencanakan masa depan, memilih jurusan, dan mengembangkan
              potensimu.
            </p>
          </div>}

        {activeTest ? renderActiveTest() : renderTestSelection()}
      </div>
    </div>;
}